// src/Experience/Utils/AssetVault/AssetProcessor.js
import * as THREE from "three";
import debug from "debug";
import { geomConfig } from "../../config/geomConfig";
import { mergeVerticesByPosition } from "../EdgeGeometryUtils.js";
import { USE_GEOMETRY_EDGES } from "../../PostProcessing/config.js";
const log = debug("app:3d");

const XUN_CONVERSION = 3.429; // 1 XUN = 3.429 mm

/** Shared edge material - reused across all meshes to reduce draw state changes */
const SHARED_EDGE_MATERIAL = new THREE.LineBasicMaterial({
  color: 0x000000,
  depthTest: true,
  depthWrite: false,
});

export default class AssetProcessor {
  constructor(experience) {
    this.idealRatio = 0;
    log("AssetProcessor instance created.");
    this.envMap = experience.environment.environmentMap;
  }

  /**
   * Main controller for processing a 3D model. Applies scale, position, and rotation corrections.
   * @param {THREE.Group | THREE.Mesh} asset - The loaded 3D asset to be processed.
   * @param {Object} params - Object containing width, height, layer, and other metadata.
   * @returns {THREE.Group | THREE.Mesh} The transformed asset ready for rendering.
   * @category Experience
   * @subcategory Utils
   */
  init(asset, params) {
    try {
      log("Starting asset processing with params:", params);
      log("Starting asset processing with asset:", asset);
      const { sizes, orientation, dimensions } = this.inspect(asset, params);

      log("Raw dimensions → X:", dimensions[0], "Y:", dimensions[1], "Z:", dimensions[2]);
      log("Assigned → width axis:", orientation.width, "height axis:", orientation.height);
      log("Interpreted sizes → width:", sizes.width, "height:", sizes.height, "thickness:", sizes.thickness);

      const scaleFactor = this.calculateScale(sizes, params);

      log("Calculated scale factor:", scaleFactor);

      
      this.scale(asset, scaleFactor);

      this.rotate(asset, params);

      this.position(asset, params, orientation);
      this.applyEdges(asset, params);
      this.applyMaterials(asset, params, this.envMap);
      log("Asset processed successfully.");
      return asset;
    } catch (error) {
      console.error("Error in AssetProcessor init:", error);
      throw error;
    }
  }

  /**
   * Inspects the asset's dimensions and identifies thickness, width, and height candidates.
   * @param {THREE.Group | THREE.Mesh} asset - The asset to inspect.
   * @returns {Object} - { dim1, dim2, thickness }
   */
  inspect(asset, params) {
    const { dimensions } = this.getBoundingBox(asset);

    const sizes = {
      width: dimensions[0],     // X axis
      thickness: dimensions[1], // Y axis
      height: dimensions[2],    // Z axis
    };

    const orientation = {
      width: 0,
      thickness: 1,
      height: 2,
    };

    // Optional: verify model's intended ratio
    const currentRatio = parseFloat((sizes.width / sizes.height).toFixed(4));
    this.idealRatio = parseFloat((params.w / params.h).toFixed(4));

    log("Raw dimensions → X:", dimensions[0], "Y:", dimensions[1], "Z:", dimensions[2]);
    log("Interpreted sizes → width:", sizes.width, "height:", sizes.height, "thickness:", sizes.thickness);
    log("Ratios → current:", currentRatio, "ideal:", this.idealRatio);

    return {
      dimensions,
      sizes,
      orientation,
    };
  }


  /**
   * Dynamically calculates the scaling factor based on the target dimensions and XUN conversion.
   * @param {number} dim1 - Candidate for width or height.
   * @param {number} dim2 - Candidate for width or height.
   * @param {Object} params - Target width and height.
   * @returns {Object} - { scaleFactor, adjustedWidth, adjustedHeight }
   */
  calculateScale(sizes, params) {
    if (!this.isGroup1(params.layer)) {
      const baseScale = 1000 / XUN_CONVERSION;
      // Layer 90 (Ruller_Quarter): 1.25% of base scale
      if (params.layer === 90) {
        return baseScale * 0.0125;
      }
      return baseScale;
    }
    const { w, h } = params;
    const realRatio = sizes.width / sizes.height;

    let scaleFactor =
      realRatio >= this.idealRatio ? w / sizes.width : h / sizes.height;

    // Layer 20 (Plates): models run small; scale up to close the gap (+3 units)
    if (params.layer === 20) {
      scaleFactor *= Math.max((w + 3) / w, (h + 3) / h);
    }

    return scaleFactor;
  }

  /**
   * Uniformly scales the asset using the provided scale factor.
   * @param {THREE.Group | THREE.Mesh} asset - The asset to scale.
   * @param {number} scaleFactor - Uniform scaling factor.
   */
  scale(asset, scaleFactor) {
    asset.scale.set(scaleFactor, scaleFactor, scaleFactor);
    log("Scaling applied with factor:", scaleFactor);
  }

  /**
   * Positions the asset after scaling based on group classification and orientation.
   * For Group 1: Centers the asset in X and Z while aligning the bottom along Y.
   * For Group 2: Keeps the original origin position unchanged.
   *
   * @param {THREE.Group | THREE.Mesh} asset - The processed 3D asset.
   * @param {Object} params - Contains layer information for group classification.
   * @param {Object} orientation - { thickness, width, height } where axes are 0 (x), 1 (y), 2 (z).
   */
  position(asset, params, orientation) {
    // Recalculate bounding box after scaling
    let { bbox } = this.getBoundingBox(asset);
    let center = bbox.getCenter(new THREE.Vector3());

    log("this is center", center);
    log("this is asset", asset);

    const minY = bbox.min.y;
    const maxY = bbox.max.y;
    // Layer 20 (Plates): put TOP surface at origin so legs extend down; Z offset +1.5
    if (params.layer === 20) {
      asset.position.set(-center.x, -maxY, -center.z + 1.5);
      return;
    }
    if (this.isGroup1(params.layer)) {
      const y = params.layer === 10 ? maxY : minY;
      // 3) Apply the final position
      asset.position.set(-center.x, -y, -center.z);
      return;
    }
    if (params.layer === 37) {
      // Housings: center in XZ and align bottom in Y so 2D/3D positions match
      asset.position.set(-center.x, -minY, -center.z);
      return;
    }
    if (params.layer === 40) {
      asset.position.set(-center.x, -minY, -center.z);
    }
    if (params.layer === 33) {
      asset.position.set(-center.x - 0.1823 / 2, -minY, -center.z);
    }
    if (params.layer === 35) {
      asset.position.set(-center.x + 0.259, -minY, -center.z);
    }
    if (params.layer === 70) {
      log(
        `this is possibly XM-LT1 ${params.partNumber === "XM-LT1-0204" ? params.pn : "nije"}`
      );
      const xPos = params.partNumber === "XM-LT1-0204" ? 0 : 0.25;
      asset.position.set(-center.x - xPos, -minY, -center.z);
    }
    if (params.layer === 50) {
      const pinBridge = ["XM-PB2-AR", "XM-PB2-ALAR", "XM-PB2-AL", "XM-PB2"];
      let isPinBridge;
      if (pinBridge.includes(params.partNumber)) {
        isPinBridge = true;
      }
      const zPosition = isPinBridge ? 0.5 : 0;
      asset.position.set(
        -params.w / 2 + 0.5,
        asset.position.y,
        asset.position.z + zPosition
      );
    }

    if (params.layer === 60) {
      log("layer60");
      log("center", center);
      const newX = center.x - 0.25;
      log("newX", newX);
      asset.position.set(-newX, -center.y, -center.z);
    }

    // Layer 90 (Ruller_Quarter): offset -0.35 in X, -0.38 in Z
    if (params.layer === 90) {
      asset.position.set(-center.x - 0.35, -minY, -center.z - 0.38);
      return;
    }

    // Adjust for Group 1: Center in X and Z, align bottom in Y
    log("Positioning applied:", asset.position);
  }

  /**
   * Rotates the asset to ensure width aligns with X, thickness with Y, and height with Z.
   * Assumes a top-down view where the top of the object is facing the negative Z direction.
   *
   * @param {THREE.Group | THREE.Mesh} asset - The processed 3D asset.
   * @param {Object} orientation - Object containing axes information for thickness, width, and height.
   */
  rotate(asset, params) {
    // Layer 10 (Controllers): flip 180° around Z so orientation matches
    if (params.layer === 10) {
      log("asset is rotateted for ", params.partNumber);
      asset.rotation.z = Math.PI;
      asset.rotation.x = 0;
      return;
    }
    // Layer 50 (Anchors): rotate 90° around Y (opposite direction)
    if (params.layer === 50) {
      asset.rotation.y = -Math.PI / 2;
      asset.rotation.x = 0;
      return;
    }
    // Layer 90 (Ruller_Quarter): rotate -90° around X
    if (params.layer === 90) {
      asset.rotation.x = -Math.PI / 2;
      return;
    }
    // All other layers: keep original model rotation
    return;
  }

  /**
   * Updates environment-specific material properties for an asset while preserving existing materials.
   * Focuses only on `metalness`, `roughness`, `envMap`, and `envMapIntensity`.
   *
   * @param {THREE.Group | THREE.Mesh} asset - The 3D asset to process.
   * @param {Object} params - Contains metadata like layer and partNumber.
   * @param {THREE.Texture} envMap - The environment map (HDRI) for reflections.
   */
  /**
   * Updates materials for an asset using MeshPhongMaterial.
   *
   * @param {THREE.Group | THREE.Mesh} asset - The 3D asset.
   * @param {Object} params - Metadata like layer and partNumber.
   * @param {THREE.Texture} envMap - Environment map (HDRI) for reflections.
   */
  applyMaterials(asset, params, envMap) {
    const layerConfig = geomConfig.layers.find(
      (layer) => layer.layer === params.layer
    );
    if (!layerConfig) {
      console.warn(`Layer configuration not found for layer ${params.layer}`);
      return;
    }

    asset.traverse((child) => {
      if (child.isMesh && child.material) {
        log(`Updating material for mesh ${child.name || "Unnamed Mesh"}`);

        const materials = Array.isArray(child.material)
          ? child.material
          : [child.material];

        const updatedMaterials = materials.map((material) => {
          // ✅ If material is already Phong or Standard, ensure transparency is preserved
          if (
            material instanceof THREE.MeshStandardMaterial ||
            material instanceof THREE.MeshPhongMaterial
          ) {
            // Ensure transparency properties are set correctly
            const currentOpacity = material.opacity !== undefined ? material.opacity : 1.0;
            const shouldBeTransparent = currentOpacity < 1.0;
            
            // Only update if transparency state doesn't match opacity value
            if (material.transparent !== shouldBeTransparent) {
              material.transparent = shouldBeTransparent;
            }
            if (material.opacity === undefined) {
              material.opacity = 1.0;
            }
            material.needsUpdate = true;
            return material;
          }

          // Apply Phong material while preserving original properties
          const newMaterial = new THREE.MeshPhongMaterial();
          newMaterial.copy(material);
          newMaterial.specular.set(0x555555); // Reduce gloss intensity
          newMaterial.shininess = 30; // Lower glossiness

          // Apply envMap only if the original material was reflective
          if (material.reflectivity > 0 || material.envMap) {
            newMaterial.envMap = envMap;
            newMaterial.reflectivity = material.reflectivity || 0.5;
          }
          // Preserve original transparency settings
          const originalOpacity = material.opacity !== undefined ? material.opacity : 1.0;
          const originalTransparent = material.transparent !== undefined ? material.transparent : (originalOpacity < 1.0);
          newMaterial.transparent = originalTransparent;
          newMaterial.opacity = originalOpacity;
          newMaterial.alphaTest = 0.01;
          newMaterial.depthWrite = true;
          newMaterial.depthTest = true;

          return newMaterial;
        });

        child.material =
          updatedMaterials.length === 1
            ? updatedMaterials[0]
            : updatedMaterials;
        child.material.needsUpdate = true;
      }
    });

    log("Materials applied successfully.");
  }

  // -------------------------
  // ✅ Helper Methods Section
  // -------------------------

  /**
   * Calculates the bounding box dimensions of the provided asset.
   * Dimensions by index: 0:x, 1:y, 2:z
   * Measures the size of the asset along the X, Y, and Z axes.
   *
   * @param {THREE.Group | THREE.Mesh} asset - The 3D asset to inspect.
   * @returns {Object} - { bbox: THREE.Box3, dimensions: [number, number, number] }
   */
  getBoundingBox(asset) {
    const bbox = new THREE.Box3().setFromObject(asset);
    const dimensions = [
      bbox.max.x - bbox.min.x,
      bbox.max.y - bbox.min.y,
      bbox.max.z - bbox.min.z,
    ];

    return { bbox, dimensions };
  }

  /**
   * Applies geometry-based edges (angle > threshold) using an improved pipeline:
   * - Position-only merge to reduce spurious diagonals from duplicate vertices
   * - 45° threshold to show only significant creases, not inner contours
   * - Shared material for better performance
   */
  applyEdges(asset, params) {
    const EDGE_THRESHOLD_ANGLE = 45; // Only edges where face angle > 45°
    const MERGE_TOLERANCE = 1e-4; // Position weld tolerance

    asset.traverse((child) => {
      if (!child.isMesh || !child.geometry) return;

      // Use a position-welded copy for edge extraction (avoids modifying mesh)
      const geomForEdges = mergeVerticesByPosition(
        child.geometry,
        MERGE_TOLERANCE
      );

      const edges = new THREE.EdgesGeometry(
        geomForEdges,
        EDGE_THRESHOLD_ANGLE
      );
      geomForEdges.dispose();

      const edgeLines = new THREE.LineSegments(
        edges,
        SHARED_EDGE_MATERIAL
      );

      edgeLines.renderOrder = child.renderOrder + 1;
      edgeLines.name = "ObjectEdge";
      edgeLines.raycast = () => {};
      // When using Sobel, geometry edges start hidden; EdgeManager will show them only if USE_GEOMETRY_EDGES
      edgeLines.visible = USE_GEOMETRY_EDGES;

      child.add(edgeLines);
    });

    log("✅ Edges applied (45° threshold, position-welded).");
  }

  /**
   * Determines if a layer belongs to Group 1.
   * @param {number} layer - The layer number to check.
   * @returns {boolean} - True if layer belongs to Group 1, otherwise false.
   */
  isGroup1(layer) {
    return layer < 33;
  }

  dispose() {
    this.envMap = null;
    this.idealRatio = 0;

    log("AssetProcessor cleanup complete: Resources released.");
  }
}
